import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

const inputCls =
  "w-full bg-[#1E2128] border border-[#2E3340] focus:border-amber-500/60 outline-none px-4 py-3.5 text-sm font-medium text-zinc-100 placeholder:text-zinc-500 transition-colors duration-300";

export default function Review() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    reference: params.get("ref") || "",
    email: "",
    name: "",
    rating: 5,
    text: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/reviews", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0F1012] grain" data-testid="review-page">
      <header className="border-b border-[#2E3340]">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Logo />
          <Link
            to="/"
            className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-zinc-400 hover:text-amber-400 transition-colors"
          >
            ← Back
          </Link>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-6 py-16 lg:py-24">
        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="border border-[#2E3340] bg-[#1E2128] p-10 text-center"
            data-testid="review-success"
          >
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-amber-500/40 mb-6">
              <Check className="text-amber-400" size={22} />
            </span>
            <h1 className="font-serif-lux text-4xl text-amber-50">Most kind.</h1>
            <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
              Your words are with our concierge. Once reviewed, they may appear on the site — thank you for riding with AUREUS.
            </p>
            <Link
              to="/"
              className="inline-block mt-8 font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] px-6 py-3 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors"
            >
              Return Home
            </Link>
          </motion.div>
        ) : (
          <>
            <p className="font-mono-lux text-[11px] font-semibold uppercase tracking-[0.4em] text-amber-500">Client Review</p>
            <h1 className="font-serif-lux font-normal text-4xl sm:text-5xl text-amber-50 mt-3">
              A few words, <span className="gold-text italic">if you would.</span>
            </h1>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
              Reviews are read personally by the house and published only after moderation.
            </p>
            <form onSubmit={submit} className="mt-10 space-y-4" data-testid="review-form">
              <input
                value={form.reference}
                onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value.toUpperCase() }))}
                placeholder="Booking reference — e.g. AUR-1A2B3C"
                className={inputCls}
                data-testid="review-reference-input"
                required
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input value={form.name} onChange={set("name")} placeholder="Your name (shown with the review)" className={inputCls} data-testid="review-name-input" required />
                <input type="email" value={form.email} onChange={set("email")} placeholder="Email used for the reservation" className={inputCls} data-testid="review-email-input" required />
              </div>
              <div className="flex items-center gap-2 py-1" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, rating: n }))}
                    data-testid={`review-star-${n}`}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star size={26} className={n <= form.rating ? "fill-amber-500 text-amber-500" : "text-zinc-700"} />
                  </button>
                ))}
                <span className="ml-2 font-mono-lux text-[10px] uppercase tracking-[0.2em] text-zinc-500">{form.rating} / 5</span>
              </div>
              <textarea
                value={form.text}
                onChange={set("text")}
                rows={4}
                placeholder="How was the journey?"
                className={`${inputCls} resize-none`}
                data-testid="review-text-input"
                required
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="review-submit-button"
                className="w-full font-mono-lux text-[11px] font-bold uppercase tracking-[0.3em] px-8 py-4 bg-amber-500 text-black hover:bg-amber-300 disabled:opacity-40 transition-colors"
              >
                {sending ? "Sending…" : "Submit review"}
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
